import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import { FavoriteService } from 'src/app/services/marketplace/favorite.service';
import { CartService } from 'src/app/services/marketplace/cart.service';
import { ListingService } from 'src/app/services/marketplace/listing.service';
import { ToastService } from 'src/app/services/toast.service';
import { Favorite, Listing } from 'src/app/services/marketplace/marketplace.models';

@Component({
  selector: 'app-favorites',
  templateUrl: './favorites.component.html',
  styleUrls: ['./favorites.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FavoritesComponent implements OnInit {
  favorites: Favorite[] = [];
  loading = true;
  /** Why the list couldn't load: an expired sign-in, or anything else. */
  loadError: 'signin' | 'other' | null = null;
  removingId: number | null = null;
  /** Listing id with an add-to-cart / claim in flight. */
  busyListingId: number | null = null;
  readonly skeletons = [1, 2, 3, 4];

  constructor(
    private favoriteService: FavoriteService,
    private cartService: CartService,
    private listingService: ListingService,
    private toaster: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.favoriteService.list().subscribe({
      next: (favorites) => {
        this.favorites = favorites;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.status === 401 ? 'signin' : 'other';
        this.cdr.detectChanges();
      },
    });
  }

  price(listing: Listing): string {
    const value = Number(listing.price);
    return value ? `$${value.toFixed(2)}` : 'Free';
  }

  remove(favorite: Favorite): void {
    this.removingId = favorite.id;
    this.favoriteService.remove(favorite.id).subscribe({
      next: () => {
        this.favorites = this.favorites.filter((f) => f.id !== favorite.id);
        this.removingId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.removingId = null;
        this.toaster.showNote('Could not remove this favorite.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  addToCart(listing: Listing): void {
    if (this.busyListingId) return;
    this.busyListingId = listing.id;
    this.cartService.addLine(listing.id, 1).subscribe({
      next: () => {
        this.busyListingId = null;
        this.toaster.showSuccess('Added to cart.', 'success');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.busyListingId = null;
        this.toaster.showNote(err?.error?.detail || err?.error?.[0] || 'Could not add this to your cart.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  /** Free items skip the cart, like on the store page. */
  claimFree(listing: Listing): void {
    if (this.busyListingId) return;
    this.busyListingId = listing.id;
    this.listingService.claim(listing.id).subscribe({
      next: () => {
        this.busyListingId = null;
        this.toaster.showSuccess('Added to your purchases and Content Vault.', 'success');
        this.router.navigate(['/marketplace/orders']);
      },
      error: (err) => {
        this.busyListingId = null;
        this.toaster.showNote(err?.error?.detail || err?.error?.[0] || 'Could not get this item.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }
}
